import React, { Fragment, useState } from 'react';

import PropTypes from 'prop-types';

import { useCurrentProjectId } from '@adsk/alloy-provider';
import { callOrReturn } from '@adsk/alloy-react-helpers';
import { useLocale } from '@adsk/alloy-react-locale';

import MissingEntities from './MissingEntities';
import ReferencePickerBlockTitle from './ReferencePickerBlockTitle';
import callUnique from '../helpers/callUnique';
import {
  entityCacheItemShape,
  pluginShape,
  relationshipShape,
} from '../helpers/propTypes';
import ReferencePickerError from '../ReferencePickerError';
import ReferencePickerLoader from '../ReferencePickerLoader';

const ReferencePickerBlockGroup = ({
  plugin,
  relationships,
  style,
  className,
  entitiesCache,
  updateEntitiesInCache,
  allowRemoveLinkedEntity,
  setDataToConfirmRemoving,
  onReferenceClickAnalytics,
  sourceEntityId,
  sourceEntityDomain,
  sourceEntityType,
}) => {
  const { T } = useLocale(({ locale }) => require(`../i18n/${locale}.json`));
  const projectId = useCurrentProjectId();
  const [previewEntity, setPreviewEntity] = useState(null);

  if (!relationships || !relationships.length) {
    return null;
  }

  const entityIds = relationships.map(({ entityId }) => entityId);
  const loading = entityIds.some((id) => entitiesCache?.[id]?.loading);
  const error = entityIds.some((id) => entitiesCache?.[id]?.error);
  const retry = () =>
    callUnique(
      entityIds.map((id) => entitiesCache?.[id]?.retry).filter((r) => r),
    );

  const data = relationships
    .map(({ relationship, entityId }) => ({
      relationship,
      entity: entitiesCache?.[entityId]?.entity,
    }))
    .filter((e) => e.entity);
  const hasData = !!data.length;
  const missingEntitiesCount = relationships.length - data.length;
  const showMissingEntitiesCount = !!missingEntitiesCount && !loading && !error;

  const { EntityBlock, EntityPreviewModal, PluralText, metadata } = plugin;
  const EmptyLoader = plugin.CustomBlockEmptyLoader || ReferencePickerLoader;
  const Error = plugin.CustomBlockError || ReferencePickerError;
  const MissingEntitiesComponent =
    plugin.CustomMissingEntities || MissingEntities;
  const showPreviewModal = !!EntityPreviewModal;

  const onUpdate = (entity) => {
    updateEntitiesInCache({
      entities: [entity],
      type: metadata.type,
      domain: metadata.domain,
    });
  };

  return (
    <div className={className} css={[style]}>
      <ReferencePickerBlockTitle>
        <PluralText />
      </ReferencePickerBlockTitle>
      {hasData && (
        <Fragment>
          <div data-testid="reference-picker-block-group">
            {data.map(({ relationship, entity }) => (
              <EntityBlock
                key={entity.id}
                entity={entity}
                projectId={projectId}
                onClick={
                  showPreviewModal ? () => setPreviewEntity(entity) : undefined
                }
                onReferenceClickAnalytics={
                  onReferenceClickAnalytics
                    ? () =>
                        onReferenceClickAnalytics({
                          id: entity.id,
                          domain: metadata.domain,
                          type: metadata.type,
                        })
                    : undefined
                }
                onRemoveClick={
                  callOrReturn(allowRemoveLinkedEntity, {
                    entity,
                    relationship,
                  })
                    ? () =>
                        setDataToConfirmRemoving({
                          entity,
                          plugin,
                          relationship,
                        })
                    : undefined
                }
                sourceEntityId={sourceEntityId}
                sourceEntityDomain={sourceEntityDomain}
                sourceEntityType={sourceEntityType}
              />
            ))}
          </div>
          {loading && <ReferencePickerLoader />}
          {error && (
            <ReferencePickerError onTryAgain={retry}>
              {T.translate('ERROR_LOADING_MORE')}
            </ReferencePickerError>
          )}
        </Fragment>
      )}
      {!hasData && loading && <EmptyLoader />}
      {!hasData && error && <Error onTryAgain={retry} />}
      {showMissingEntitiesCount && (
        <MissingEntitiesComponent
          count={missingEntitiesCount}
          relationshipCount={relationships.length}
        />
      )}
      {showPreviewModal && (
        <EntityPreviewModal
          entity={previewEntity}
          open={!!previewEntity}
          onUpdate={onUpdate}
          onClose={() => setPreviewEntity(null)}
        />
      )}
    </div>
  );
};

ReferencePickerBlockGroup.propTypes = {
  /** Styles applied to the root element */
  style: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  /** Class applied to the root element */
  className: PropTypes.string,
  /** The plugin to use to render the group */
  plugin: pluginShape.isRequired,
  /** Array of relationships to show in this group */
  relationships: PropTypes.arrayOf(relationshipShape),
  /** An object containing all retrieved entities of current type */
  entitiesCache: PropTypes.objectOf(entityCacheItemShape),
  /** Handler for remove entity click event */
  setDataToConfirmRemoving: PropTypes.func.isRequired,
  /** Whether or not remove link is allowed for linked entities. Can be bool for all or func to set it for each entity */
  allowRemoveLinkedEntity: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.func,
  ]),
  /**
   * A handle method to update entities in the cache.
   * The handler is of shape { entities, domain, type } => *
   */
  updateEntitiesInCache: PropTypes.func,
  /**
   * Callback fired when user clicks a specific reference. This callback doesn't block the native click support.
   * The general use case is for handling analytics on reference clicks.
   * Handler of shape: ({ type, domain, id }) => *
   */
  onReferenceClickAnalytics: PropTypes.func,
  /** The entity ID we link to */
  sourceEntityId: PropTypes.string.isRequired,
  /** The source entity domain, as used by relationship service */
  sourceEntityDomain: PropTypes.string.isRequired,
  /** The source entity type, as used by relationship service */
  sourceEntityType: PropTypes.string.isRequired,
};

export default ReferencePickerBlockGroup;
